import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BillType } from '@features/bills/enums/billType'
import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { PaymentRegistrationSuccessState } from '@features/paymentReconciliationModal/enums/paymentRegistrationSuccessState'
import { useCreateSubjectsAndBankLinesReconciliation } from '@features/paymentReconciliationModal/hooks'
import { convertSubjectsIdsToReconciliationSubjects } from '@features/paymentReconciliationModal/utils/convertSubjectsIdsToReconciliationSubjects'
import { notifyPaymentRegistrationError } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationError'
import { notifyPaymentRegistrationSuccess } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationSuccess'
import { BankMatchUiFlowValue, UiSourceValue } from '@features/reconciliation/enums'
import {
  useBankLineSubjectAssociations,
  useDeleteBankLineSubjectAssociations,
} from '@modules-deprecated/bankReconciliation/hooks'
import { DifferenceType } from '@modules-deprecated/bankReconciliation/types'

export interface UseReconcileBillProps {
  billId?: string
  onSuccess?: () => void
  selectedDifferenceType?: DifferenceType
  organizationId?: string | null
  differenceType?: DifferenceType
  crossCurrencyPaymentAmount?: number | null
  bankLineId?: string
  isAutoMatch?: boolean
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

export const useReconcileBill = ({
  billId,
  onSuccess,
  differenceType,
  organizationId,
  crossCurrencyPaymentAmount,
  bankLineId,
  isAutoMatch,
  uiSource,
  uiFlow,
}: UseReconcileBillProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const { bankLineSubjectAssociations: billSubjectAssociations } = useBankLineSubjectAssociations({
    subjectId: billId,
    subjectType: BillType.Bill,
  })
  const { t } = useTranslation()

  const { create: createBillsAndBankLinesReconciliation } = useCreateSubjectsAndBankLinesReconciliation({
    onSuccess: () => {
      onSuccess?.()
      notifyPaymentRegistrationSuccess(PaymentRegistrationSuccessState.RegisteredAndReconciled, t)
      invalidateBill(billId)
      invalidateBills()
      setIsProcessing(false)
    },
    onError: () => () => {
      setIsProcessing(false)
      notifyPaymentRegistrationError(t)
    },
  })
  const { deleteAssociations } = useDeleteBankLineSubjectAssociations()

  const { invalidateBills, invalidateBill } = useInvalidateBills()

  const reconcileSubject = () => {
    if (!billId || !organizationId || !bankLineId) {
      return
    }

    setIsProcessing(true)
    const associationIds = billSubjectAssociations.map(({ id }) => id)
    deleteAssociations(associationIds, {
      onSuccess: () => {
        createBillsAndBankLinesReconciliation({
          bankLinesIds: [bankLineId],
          organizationId,
          subjects: convertSubjectsIdsToReconciliationSubjects([billId], BillType.Bill),
          parameters: {
            differenceType,
            ...(crossCurrencyPaymentAmount && { expectedAmount: crossCurrencyPaymentAmount }),
            ...(isAutoMatch && { isAutoMatch }),
          },
          uiSource,
          uiFlow,
        })
      },
    })
  }

  return {
    reconcileSubject,
    isProcessing,
  }
}
